/** @format */

import { AsyncPipe, CurrencyPipe, NgClass } from '@angular/common';
import {
	AttributeOption,
	selectColorToResolvedSkuMap,
	selectDisabledAttributes,
	selectSelectedColorAttribute
} from '@pdp/attributes/data-access';
import { BreakpointsService, Scene7Service } from '@shared/data-access/services';
import {
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
	Signal,
	ViewChild,
	computed,
	inject
} from '@angular/core';
import {
	HmfButtonComponent,
	HmfDrawerComponent,
	HmfSelectorComponent
} from '@dsg-tech/homefield-angular';
import {
	HomefieldIconLibraryModule,
	HomefieldIconsLibraryService,
	homefieldIconAlertInfoFilled
} from '@dsg-tech/homefield-angular-icons';
import { take, takeUntil } from 'rxjs/operators';

import { BreakPointNew } from '@shared/data-access/enums';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { selectAllProductStyle } from '@pdp/data-access/state';

@Component({
	selector: 'pdp-attributes-components-color-attribute-panel',
	templateUrl: './color-attributes-panel.component.html',
	styleUrl: './color-attributes-panel.component.scss',
	imports: [
		HmfSelectorComponent,
		HomefieldIconLibraryModule,
		HmfDrawerComponent,
		NgClass,
		HmfButtonComponent,
		AsyncPipe,
		CurrencyPipe
	]
})
export class ColorAttributesPanelComponent implements OnInit, OnDestroy {
	@Input() colorAttributeData: AttributeOption[];
	@Input() colorQueryParam = '';
	@Input() skuQueryParam = '';

	@Output() selectedAttributeOnClick: EventEmitter<AttributeOption> =
		new EventEmitter<AttributeOption>();

	@ViewChild(HmfDrawerComponent) drawerComponent: HmfDrawerComponent | undefined;

	private readonly store = inject(Store);
	private readonly scene7Service = inject(Scene7Service);
	private readonly breakpointsService = inject(BreakpointsService);

	selectedColor$ = this.store.select(selectSelectedColorAttribute);
	colorToResolvedSkuMap$ = this.store.select(selectColorToResolvedSkuMap);
	selectDisabledAttributes$ = this.store.select(selectDisabledAttributes);

	UNSUBSCRIBE$: Subject<void> = new Subject();
	productPartNumber = '';
	selectedColorThumbnailImageSource = '';
	selectedColor: AttributeOption;
	isMobile: Signal<boolean> = computed(
		() => this.breakpointsService.breakpoint() <= BreakPointNew.SMALL
	);
	drawerDirection: Signal<'right' | 'bottom'> = computed(() =>
		this.isMobile() ? 'bottom' : 'right'
	);

	constructor(homefieldIconsLibraryService: HomefieldIconsLibraryService) {
		homefieldIconsLibraryService.registerIcons([homefieldIconAlertInfoFilled]);
	}

	ngOnInit(): void {
		this.store
			.select(selectAllProductStyle)
			.pipe(take(1))
			.subscribe((productStyle) => {
				this.productPartNumber = productStyle.partNumber;
			});

		if (!this.colorQueryParam && !this.skuQueryParam) {
			this.selectedAttribute(this.colorAttributeData[0]);
		} else if (this.colorQueryParam) {
			const queryParamColor = this.colorAttributeData.find(
				(color) => color.value.replaceAll(' ', '_') === this.colorQueryParam
			);
			if (queryParamColor) {
				this.selectedAttribute(queryParamColor);
			} else {
				this.selectedAttribute(this.colorAttributeData[0]);
			}
		}

		this.selectedColor$.pipe(takeUntil(this.UNSUBSCRIBE$)).subscribe((selectedColor) => {
			this.selectedColor = selectedColor;
			this.selectedColorThumbnailImageSource = this.setColorImageSrc(
				this.selectedColor.value.replace(/[^\w]/gi, '_')
			);
		});
	}

	imageErrorFallback(event): void {
		event.target.src =
			'https://images.dickssportinggoods.com/assets/icons/dsg/ImageUnavailablePDPSwatch.svg';
	}

	openModal(): void {
		this.drawerComponent?.openDrawer();
	}

	/**
	 * @description Closes the color attribute modal
	 **/
	closeModal(): void {
		this.drawerComponent?.closeDrawer();
	}

	/**
	 * @description Emits an event when a color swatch is clicked
	 * and updates the current swatch to be selected.
	 **/
	selectedAttribute(selectedAttributeValue: AttributeOption): void {
		this.selectedAttributeOnClick.emit(selectedAttributeValue);

		if (this.isMobile()) {
			this.drawerComponent?.closeDrawer();
		}
	}

	/**
	 * @description Builds the color swatch image url and returns it.
	 **/
	setColorImageSrc(colorName: string): string {
		const imageParams = '?$&resMode=bilin&qlt=80&hei=75&wid=75&op_sharpen=1';

		return this.scene7Service.getProductImageUrl(
			this.productPartNumber,
			'productbean',
			colorName.replace(/[^\w]/gi, '_'),
			imageParams
		);
	}

	ngOnDestroy(): void {
		this.UNSUBSCRIBE$.next();
		this.UNSUBSCRIBE$.complete();
	}

	trackByFn(index: number, item: AttributeOption): string {
		return index.toString() + item.name;
	}
}
